'use client';

import {
  ChangeEvent,
  DetailedHTMLProps,
  ForwardedRef,
  forwardRef,
  InputHTMLAttributes,
  ReactNode,
  TextareaHTMLAttributes,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';

import { tv } from 'tailwind-variants';
import { v4 } from 'uuid';

import TextSmall from '@/components/Typography/TextSmall';
import cn from '@/helpers/utils/classNames';

export type TextInputTypes = 'text' | 'number' | 'email' | 'password' | 'textarea';
type CustomInput = Omit<DetailedHTMLProps<InputHTMLAttributes<HTMLInputElement>, HTMLInputElement>, 'type' | 'size'>;
export interface TextInputProps extends CustomInput {
  type: TextInputTypes;
  variant: 'boxed' | 'rounded' | 'compact';
  size?: 'default' | 'lg';
  outline?: 'default' | 'primary';
  children?: ReactNode;
  onMount?: () => void;
  label?: string | ReactNode;
  inputClassName?: string;
  error?: string;
  errorClassName?: string;
  validator?: (value: string) => boolean;
}
export const textInputStyle = tv({
  base: 'v2-h-[44px] v2-rounded v2-px-[14px] v2-py-[10px] v2-border-[#D0D5DD] v2-shadow-xs focus:v2-border-primary focus:v2-shadow-input-focus-primary v2-text-base v2-text-[#101828] v2-font-sweet-sans-pro-light v2-font-light mt-1 w-full v2-outline-none',
  variants: {
    variant: {
      boxed: 'border',
      compact: 'rounded-none border-b focus:shadow-none bg-transparent',
      rounded: 'border px-4',
    },
    size: {
      default: '',
      lg: 'text-base',
    },
    outline: {
      default: '',
      primary: 'focus:border-primary focus:shadow-input-focus-primary',
    },
  },
});

const TextInput = forwardRef<
  { validate: () => Promise<boolean> } & Partial<HTMLInputElement | HTMLTextAreaElement>,
  TextInputProps
>(function TextInput(props, ref) {
  const [data, setData] = useState<{
    isValidationFailed: boolean;
    value: string;
  }>({
    isValidationFailed: false,
    value: '',
  });

  const inputRef = useRef<HTMLInputElement | HTMLTextAreaElement>();

  const {
    type,
    label,
    children,
    variant,
    className,
    size,
    outline,
    onMount,
    inputClassName,
    onChange,
    validator,
    value,
    error,
    errorClassName,
    ...restProps
  } = props;

  useEffect(() => {
    if (onMount) onMount();
  }, [onMount]);

  useEffect(() => {
    const formattedValue = !value ? '' : value;
    if (formattedValue !== data.value) setData(prev => ({ ...prev, value: formattedValue as string }));
  }, [data, value]);

  useImperativeHandle(
    ref,
    () => ({
      ...(inputRef.current || {}),
      validate: async () => {
        if (!validator) return false;

        return new Promise<boolean>(resolve =>
          setData(prev => {
            const isValidationFailed = !validator(prev.value.toString());
            resolve(isValidationFailed);
            return { ...prev, isValidationFailed };
          })
        );
      },
    }),
    [validator]
  );
  const textId = v4();

  const showerror = (!validator && error) || (!!validator && data.isValidationFailed && error);

  return (
    <div
      className={cn(`relative h-fit w-full font-circular-spotify-text text-base`, {
        'w-auto': className?.includes('w-'),
      })}
    >
      {label && (
        <label
          htmlFor={textId}
          className={cn('ml-1 block font-light text-grayscale-95', {
            'font-circular-std-medium': outline === 'primary',
          })}
        >
          {label}
        </label>
      )}
      {type !== 'textarea' ? (
        <input
          id={textId}
          ref={inputRef as ForwardedRef<HTMLInputElement>}
          type={type}
          className={cn(
            textInputStyle({ variant, size, outline }),
            {
              '[appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none':
                type == 'number',
              'border !border-red-500 focus:!border-red-500 focus:!shadow-none': showerror,
            },
            inputClassName,
            className
          )}
          value={data.value || ''}
          onChange={e => {
            const { value } = e.target;
            onChange && onChange(e);
            setData(prev => {
              if (validator) {
                return { ...prev, value, isValidationFailed: !validator(value) };
              }
              return { ...prev, value };
            });
          }}
          {...restProps}
        />
      ) : (
        <textarea
          ref={inputRef as ForwardedRef<HTMLTextAreaElement>}
          id={textId}
          className={cn(
            textInputStyle({ variant, size, outline }),
            { 'rounded-lg': variant === 'rounded' },
            'w-full',
            className
          )}
          rows={4}
          value={data.value || ''}
          onChange={e => {
            const { value } = e.target;
            setData(prev => {
              if (validator) {
                return { ...prev, value, isValidationFailed: validator(value) };
              }
              return { ...prev, value };
            });
            onChange && onChange(e as unknown as ChangeEvent<HTMLInputElement>);
          }}
          {...(restProps as Omit<
            DetailedHTMLProps<TextareaHTMLAttributes<HTMLTextAreaElement>, HTMLTextAreaElement>,
            'type' | 'size'
          >)}
        ></textarea>
      )}
      {children && <div className="inline-block">{children}</div>}
      {showerror && (
        <TextSmall variant="danger" className={cn('mt-[6px]', errorClassName)}>
          {error}
        </TextSmall>
      )}
    </div>
  );
});

export default TextInput;
